<script lang="ts">
  import XIcon from '@lucide/svelte/icons/x'
  import LoaderCircleIcon from '@lucide/svelte/icons/loader-circle'
  import CheckIcon from '@lucide/svelte/icons/check'
  import AlertCircleIcon from '@lucide/svelte/icons/alert-circle'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import {
    operationToasts,
    dismissOperationToast,
    phaseLabel,
    type OperationToast,
  } from '$lib/operations.svelte'

  let now = $state(Date.now())

  $effect(() => {
    const dismissing = operationToasts.some((t) => t.dismissEndsAt != null)
    const active = operationToasts.some(
      (t) => (t.status === 'pending' || t.status === 'running') && t.details?.progressPercent != null
    )
    if (!dismissing && !active) return
    const id = setInterval(() => {
      now = Date.now()
    }, 50)
    return () => clearInterval(id)
  })

  function statusLabel(t: OperationToast) {
    switch (t.status) {
      case 'pending':
        return 'Queued'
      case 'running':
        return 'In progress'
      case 'done':
        return 'Completed'
      case 'failed':
        return 'Failed'
    }
  }

  /** Remaining dismiss time as 100 → 0. */
  function dismissProgress(t: OperationToast) {
    if (t.dismissEndsAt == null || t.dismissMs == null || t.dismissMs <= 0) return 0
    const remaining = Math.max(0, t.dismissEndsAt - now)
    return (remaining / t.dismissMs) * 100
  }

  function progressValue(t: OperationToast) {
    const pct = t.details?.progressPercent
    if (pct == null || t.status === 'done' || t.status === 'failed') return undefined
    return Math.min(100, Math.max(0, pct))
  }

  function libraryLine(t: OperationToast) {
    const d = t.details
    if (!d?.library) return undefined
    if (d.libraryTotal && d.libraryIndex) {
      return `Library ${d.libraryIndex}/${d.libraryTotal}: ${d.library}`
    }
    return d.library
  }

  function seriesLine(t: OperationToast) {
    const d = t.details
    if (!d?.series) return undefined
    if (d.seriesTotal && d.seriesIndex) {
      return `Series ${d.seriesIndex}/${d.seriesTotal}: ${d.series}`
    }
    return d.series
  }
</script>

{#if operationToasts.length > 0}
  <div
    class="pointer-events-none fixed top-20 right-4 z-[1000] flex w-[min(calc(100vw-2rem),22rem)] flex-col gap-2"
    aria-live="polite"
    aria-relevant="additions removals"
  >
    {#each operationToasts as toast (toast.id)}
      {@const progress = progressValue(toast)}
      {@const libLine = libraryLine(toast)}
      {@const serLine = seriesLine(toast)}
      {@const phase = phaseLabel(toast.details?.phase)}
      <div
        class="pointer-events-auto border-border bg-card text-card-foreground animate-in fade-in slide-in-from-right-4 relative flex gap-3 overflow-hidden rounded-lg border p-3 pb-3.5 shadow-md duration-200"
        role="status"
      >
        <div class="mt-0.5 shrink-0">
          {#if toast.status === 'pending' || toast.status === 'running'}
            <LoaderCircleIcon
              class="size-4 animate-spin
                {toast.status === 'running'
                  ? 'text-latte-sky dark:text-macchiato-sky'
                  : 'text-muted-foreground'}"
            />
          {:else if toast.status === 'done'}
            <CheckIcon class="size-4 text-emerald-600 dark:text-emerald-400" />
          {:else}
            <AlertCircleIcon class="text-destructive size-4" />
          {/if}
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <p class="text-sm leading-snug font-medium">{toast.title}</p>
            <button
              type="button"
              class="text-muted-foreground hover:text-foreground -mt-0.5 -me-1 shrink-0 rounded p-0.5"
              aria-label="Dismiss"
              onclick={() => dismissOperationToast(toast.id)}
            >
              <XIcon class="size-3.5" />
            </button>
          </div>
          {#if toast.description}
            <p class="text-muted-foreground mt-0.5 truncate text-xs">{toast.description}</p>
          {/if}

          {#if toast.status === 'running' && (phase || libLine || serLine || progress != null)}
            <div class="bg-muted/40 mt-2 space-y-1.5 rounded-md px-2.5 py-2">
              {#if phase}
                <p class="text-latte-sky dark:text-macchiato-sky text-[11px] font-medium tracking-wide uppercase">
                  {phase}
                </p>
              {/if}
              {#if libLine}
                <p class="truncate text-xs leading-snug">{libLine}</p>
              {/if}
              {#if serLine}
                <p class="text-muted-foreground truncate text-xs leading-snug">{serLine}</p>
              {/if}
              {#if progress != null}
                <div class="flex items-center gap-2 pt-0.5">
                  <Progress
                    value={progress}
                    max={100}
                    class="h-1.5 flex-1 bg-muted [&_[data-slot=progress-indicator]]:bg-latte-sky dark:[&_[data-slot=progress-indicator]]:bg-macchiato-sky"
                    aria-label="Operation progress"
                  />
                  <span class="text-muted-foreground shrink-0 text-[11px] tabular-nums">
                    {Math.round(progress)}%
                  </span>
                </div>
              {/if}
            </div>
          {:else}
            <p
              class="mt-1 text-xs
                {toast.status === 'failed'
                  ? 'text-destructive'
                  : toast.status === 'done'
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : toast.status === 'running'
                      ? 'text-latte-sky dark:text-macchiato-sky'
                      : 'text-muted-foreground'}"
            >
              {#if toast.status === 'failed' && toast.error}
                {toast.error}
              {:else}
                {statusLabel(toast)}
              {/if}
            </p>
          {/if}
        </div>
        {#if toast.dismissEndsAt != null}
          <Progress
            value={dismissProgress(toast)}
            max={100}
            class="absolute inset-x-0 bottom-0 h-1 rounded-none bg-muted
              {toast.status === 'failed'
                ? '[&_[data-slot=progress-indicator]]:bg-destructive'
                : '[&_[data-slot=progress-indicator]]:bg-emerald-600 dark:[&_[data-slot=progress-indicator]]:bg-emerald-400'}"
            aria-label="Dismiss countdown"
          />
        {/if}
      </div>
    {/each}
  </div>
{/if}
